import { getAuth, signOut, type Auth } from 'firebase/auth'

// Keep a reference to the auth instance for reuse
let authInstance: Auth | null = null

// Token caching to reduce API calls
interface TokenCache {
  token: string
  expiryTime: number // Expiry timestamp in milliseconds
}

// Token refresh in progress tracking
let tokenRefreshInProgress = false
let lastRefreshTime = 0
const TOKEN_REFRESH_COOLDOWN = 5000 // 5 seconds in ms
let cachedToken: TokenCache | null = null

// Export a function that doesn't immediately use inject/composables
export function useAuthSession() {
  // Use getAuth() directly to avoid inject() calls
  const getAuthInstance = () => {
    if (authInstance) return authInstance

    try {
      // Only use the composable in component contexts
      if (import.meta.client) {
        authInstance = getAuth()
        return authInstance
      }
    }
    catch (error) {
      console.error('Error getting auth instance:', error)
    }
    return null
  }

  /**
   * Get a valid ID token with caching to minimize API calls
   * Only forces a refresh if the token is expired or forceRefresh=true
   */
  const getAuthToken = async (forceRefresh = false): Promise<string | null> => {
    const auth = getAuthInstance()
    if (!auth || !auth.currentUser) return null

    const now = Date.now()

    // Return cached token if it's still valid and no force refresh is requested
    if (!forceRefresh
      && cachedToken
      && now < cachedToken.expiryTime - 5 * 60 * 1000) { // 5 min buffer before expiry
      return cachedToken.token
    }

    // If a refresh is already in progress, wait a bit and use cached token
    if (tokenRefreshInProgress) {
      if (cachedToken) return (cachedToken as TokenCache).token

      // Wait for the other refresh to complete (with timeout)
      let attempts = 0
      while (tokenRefreshInProgress && attempts < 10) {
        await new Promise(resolve => setTimeout(resolve, 300))
        attempts++
      }

      if (cachedToken) return (cachedToken as TokenCache).token
    }

    // Check if we've refreshed too recently (prevent refresh spam)
    if (now - lastRefreshTime < TOKEN_REFRESH_COOLDOWN && cachedToken) {
      return cachedToken.token
    }

    try {
      tokenRefreshInProgress = true
      lastRefreshTime = now

      // Get fresh token
      const token = await auth.currentUser.getIdToken(forceRefresh)

      // Cache the token with expiry time (Firebase tokens typically last 1 hour)
      cachedToken = {
        token,
        expiryTime: now + 55 * 60 * 1000, // 55 minutes - slightly less than the typical 1 hour
      }

      return token
    }
    catch (error) {
      console.error('Error getting auth token:', error)
      return null
    }
    finally {
      tokenRefreshInProgress = false
    }
  }

  /**
   * Check if the current user session is valid without always forcing a token refresh
   */
  const checkSession = async () => {
    const auth = getAuthInstance()

    if (!auth || !auth.currentUser) {
      return false
    }

    try {
      // Get token but don't force refresh unless needed
      const token = await getAuthToken(false)
      return !!token
    }
    catch (error) {
      console.error('Session invalid:', error)
      return false
    }
  }

  // Refresh the session
  const refreshSession = async () => {
    const auth = getAuthInstance()

    if (!auth || !auth.currentUser) {
      return false
    }

    try {
      // Force refresh the token
      const token = await getAuthToken(true)
      if (!token) return false

      // Exchange for a new session cookie using $fetch
      await $fetch('/api/auth/create-session', {
        method: 'POST',
        body: {
          idToken: token,
          expiresIn: 60 * 60 * 24 * 14 * 1000, // 14 days
        },
      })

      return true
    }
    catch (error) {
      console.error('Failed to refresh session:', error)
      return false
    }
  }

  // Handle session logout
  const clearSession = async () => {
    try {
      console.warn('Clearing session...')
      // Call logout endpoint to clear cookie
      await $fetch('/api/auth/logout', { method: 'POST' })

      // Sign out from Firebase if available
      const auth = getAuthInstance()
      if (auth) {
        await signOut(auth)
      }

      // Clear token cache on logout
      cachedToken = null
      return true
    }
    catch (error) {
      console.error('Error clearing session:', error)
      return false
    }
  }

  return {
    checkSession,
    refreshSession,
    clearSession,
    getAuthToken, // Expose this for use in other places
  }
}
